<script setup lang="ts">
import { ref, computed } from 'vue';
import type { ProductBundle, ProductBundleStep } from '@features/bundle';
import UiButtonMain from '@shared/ui/ButtonMain.vue';

const props = defineProps<{
  bundles: ProductBundle[];
  /** Картинка текущего товара — показывается основным блюдом во всех мини-сценах */
  baseProductImg?: string | null;
}>();

const emit = defineEmits<{
  (e: 'select', bundle: ProductBundle): void;
}>();

const containerRef = ref<any>(null);

// ─── Scene helpers ─────────────────────────────────────────────────────────

const getDefaultImg = (step: ProductBundleStep): string | null => {
  const product = step.products.find((p) => p.is_default) ?? step.products[0];
  if (!product?.img) return null;
  if (typeof product.img === 'string') return product.img;
  if (Array.isArray(product.img)) return product.img[0]?.url ?? null;
  return null;
};

interface BundleScene {
  bundle: ProductBundle;
  hasSteps: boolean;
  drinkImg: string | null;
  snackImg: string | null;
  sauce1Img: string | null;
  sauce2Img: string | null;
}

const bundlesWithScenes = computed<BundleScene[]>(() =>
  props.bundles.map((bundle) => {
    const steps = bundle.steps ?? [];
    const drinkStep = steps.find((s) => s.step_type === 'drink') ?? null;
    const snackStep = steps.find((s) => s.step_type === 'snack') ?? steps.find((s) => s.step_type === 'other') ?? null;
    const sauceSteps = steps.filter((s) => s.step_type === 'sauce');

    return {
      bundle,
      hasSteps: steps.length > 0,
      drinkImg: drinkStep ? getDefaultImg(drinkStep) : null,
      snackImg: snackStep ? getDefaultImg(snackStep) : null,
      sauce1Img: sauceSteps[0] ? getDefaultImg(sauceSteps[0]) : null,
      sauce2Img: sauceSteps[1] ? getDefaultImg(sauceSteps[1]) : null,
    };
  }),
);
</script>

<template>
  <div class="bundles-section">
    <div class="bundles-section__title">Собрать набор</div>
    <ClientOnly>
      <swiper-container
        ref="containerRef"
        class="bundles-section__swiper"
        space-between="12"
        slides-per-view="auto"
        :free-mode="true"
      >
        <swiper-slide
          v-for="{ bundle, hasSteps, drinkImg, snackImg, sauce1Img, sauce2Img } in bundlesWithScenes"
          :key="bundle.id"
          class="bundles-section__slide"
        >
          <div class="bundles-section__card" role="button" tabindex="0" @click="emit('select', bundle)">
            <div class="bundles-section__card-img-wrap">
              <!-- ── Составная мини-сцена из товаров шагов ── -->
              <div v-if="hasSteps" class="bundles-section__mini-scene">
                <!-- Снэк — правый задний план -->
                <img
                  v-if="snackImg"
                  :src="snackImg"
                  class="bundles-section__mini-item bundles-section__mini-snack"
                  alt=""
                />
                <!-- Соус 2 — правый передний план левее -->
                <img
                  v-if="sauce2Img"
                  :src="sauce2Img"
                  class="bundles-section__mini-item bundles-section__mini-sauce bundles-section__mini-sauce--2"
                  alt=""
                />
                <!-- Соус 1 — правый передний план правее -->
                <img
                  v-if="sauce1Img"
                  :src="sauce1Img"
                  class="bundles-section__mini-item bundles-section__mini-sauce bundles-section__mini-sauce--1"
                  alt=""
                />
                <!-- Напиток — центр -->
                <img
                  v-if="drinkImg"
                  :src="drinkImg"
                  class="bundles-section__mini-item bundles-section__mini-drink"
                  alt=""
                />
                <!-- Основное блюдо — текущий товар из модалки, слева большое -->
                <img
                  v-if="baseProductImg || bundle.hero_image"
                  :src="baseProductImg || bundle.hero_image || ''"
                  class="bundles-section__mini-item bundles-section__mini-main"
                  alt=""
                />
              </div>
              <!-- ── Фоллбэк: обычная картинка ── -->
              <img v-else :src="bundle.hero_image || ''" :alt="bundle.name" class="bundles-section__card-img" />
            </div>
            <div class="bundles-section__card-body">
              <span class="bundles-section__card-name">{{ bundle.name }}</span>
              <UiButtonMain
                :label="`от ${bundle.base_price} ₽`"
                color="red"
                size="sm"
                variant="solid"
                :aria-label="`Собрать набор от ${bundle.base_price} рублей`"
                class="bundles-section__card-price"
              />
            </div>
          </div>
        </swiper-slide>
      </swiper-container>
    </ClientOnly>
  </div>
</template>

<style scoped lang="scss">
.bundles-section {
  @apply flex flex-col gap-3 min-w-0 w-full;

  &__title {
    @apply text-base font-semibold text-hui-foreground;
  }

  &__scroll {
    @apply flex gap-3 overflow-x-auto pb-1;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__swiper {
    @apply w-full min-w-0 max-w-full;
  }

  &__slide {
    @apply w-auto h-auto;
  }

  &__card {
    @apply flex-shrink-0 flex flex-col rounded-2xl overflow-hidden border border-hui-divider;
    @apply bg-hui-content2 w-36 text-left cursor-pointer;
    @apply transition-transform active:scale-95 h-full mt-auto;

    &:hover {
      @apply border-hui-danger;
    }
  }

  &__card-img-wrap {
    @apply w-full h-24 overflow-hidden bg-hui-content3;
  }

  &__card-img {
    @apply w-full h-full object-cover;
  }

  // ── Мини-составная сцена ──────────────────────────────────────────────────
  &__mini-scene {
    @apply relative w-full h-full;
  }

  &__mini-item {
    position: absolute;
    bottom: 0;
    object-fit: contain;
    user-select: none;
    pointer-events: none;
  }

  // Основное блюдо — слева, крупное
  &__mini-main {
    left: 2%;
    bottom: -15%;
    height: 92%;
    max-width: 54%;
    z-index: 3;
    filter: drop-shadow(2px 4px 6px rgba(0, 0, 0, 0.22));
  }

  // Напиток — центр
  &__mini-drink {
    left: 24%;
    bottom: 12%;
    height: 84%;
    max-width: 58%;
    z-index: 2;
    filter: drop-shadow(1px 2px 5px rgba(0, 0, 0, 0.18));
  }

  // Снэк — правый задний план
  &__mini-snack {
    right: 6%;
    bottom: 8%;
    height: 64%;
    max-width: 46%;
    z-index: 3;
    filter: drop-shadow(1px 1px 4px rgba(0, 0, 0, 0.14));
  }

  // Соусы — правый передний план
  &__mini-sauce {
    height: 36%;
    max-width: 24%;
    z-index: 5;
    filter: drop-shadow(1px 2px 3px rgba(0, 0, 0, 0.15));

    &--1 {
      right: 3%;
      z-index: 6;
    }

    &--2 {
      right: 21%;
      z-index: 5;
    }
  }

  &__card-body {
    @apply grid gap-1 p-2 mt-auto;
  }

  &__card-name {
    @apply text-xs text-hui-foreground/70 leading-tight line-clamp-2 text-center;
  }

  &__card-price {
    @apply text-sm font-bold mt-1;
  }
}
</style>
